import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { useSelector } from 'store';
import useLocalization from 'hooks/useLocalization';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { IconClipboardList } from '@tabler/icons-react';
import ContactSupportModal from 'components/ContactSupportModal';
import { useSupportDiagnosticOptional } from 'features/SupportMode';
import { revealOnboardingChecklist } from 'hooks/useOnboardingChecklist';
import { closeGlobalAppHelpFloating } from 'hooks/useGlobalAppHelpFloating';
import { clearAiSessionInBrowser, loadAiSessionFromBrowser, saveAiSessionToBrowser } from 'utils/ai-session-storage';
import { loadConnectedAiApiKeyIntegrationsFromBrowser } from 'utils/ai-integrations-browser-storage';
import { askCompanyAiSupportChat, getCompanyAiStatus, type CompanyAiProvider } from 'api/company-ai';
import { askLocalLlmConversationWithSystem, type LocalChatTurn } from 'utils/ask-ai-local-llm-chat';
import { fetchGeminiModelsForGenerateContent } from 'utils/gemini-list-models';
import { buildAppTechnicalSupportSystemPrompt } from 'utils/ai-context-engine';
import { generateUniqueId } from 'utils/helpers';
import { INTEGRATION_DEFINITIONS } from 'views/integrations/connections/integration-definitions';
import type { IntegrationConnectionRecord } from 'types/integrations/integration-connection';
import type { AskAIDialogMessageType } from 'types/chat/props';
import {
  AIAssistantPane,
  type AIAssistantPaneMessage,
} from 'ui-component/ai-assistant';
import {
  technicalSupportAppShellProps,
  technicalSupportFloatingAvatarSx,
  technicalSupportFloatingBrandHeaderSx,
  technicalSupportFloatingHeaderIconButtonSx,
  technicalSupportFloatingMessageAreaSx,
} from './technicalSupportPanelSkin';

const GEMINI_FALLBACK_MODEL = 'gemini-2.5-flash';
const LAST_INTEGRATION_SESSION_KEY = 'berry.ask-ai.last-integration-id';
const GLOBAL_APP_HELP_SESSION_ID = 'global_app_help';
/**
 * «חיבור» וירטואלי: מפתח ה-AI של החברה ששמור בשרת (CompanyAiKeys). השיחה עוברת
 * דרך הנוד (/api/ai/support-chat) והמפתח לא מגיע לדפדפן. עד 1.11 הפאנל הכיר רק
 * מפתחות מ-localStorage, ולכן אמר «אין חיבורים» למי שהמפתח שלו נשמר בשרת.
 */
const COMPANY_ENGINE_ID = 'company_server_engine';
const COMPANY_ENGINE_LABELS: Record<CompanyAiProvider, string> = {
  anthropic: 'Anthropic Claude',
  google: 'Google Gemini',
};

type CompanyEngineState =
  | { loading: true; ready: false; provider: null; model: null }
  | { loading: false; ready: boolean; provider: CompanyAiProvider | null; model: string | null };

function definitionModelOptions(integrationId: string): string[] {
  return INTEGRATION_DEFINITIONS.find((d) => d.id === integrationId)?.modelOptions ?? [];
}

function messagesToTurns(msgs: AskAIDialogMessageType[]): LocalChatTurn[] {
  return msgs
    .filter((m) => m.message !== 'loading')
    .map((m) => ({
      role: m.sender === 'user' ? ('user' as const) : ('assistant' as const),
      content: m.message,
    }));
}

const GlobalAppTechnicalSupportAI: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentLocale, currentLanguage } = useLocalization();
  const h = currentLocale?.header?.appHelp;
  const { enqueueSnackbar } = useSnackbar();

  const meUser = useSelector((s) => s.user);
  const persistLocalAiHistory = useSelector((s) => s.userConfig.persistLocalAiHistory !== false);

  const [localConnections, setLocalConnections] = React.useState<IntegrationConnectionRecord[]>([]);
  const [companyEngine, setCompanyEngine] = React.useState<CompanyEngineState>({
    loading: true,
    ready: false,
    provider: null,
    model: null,
  });
  const [selectedIntegrationId, setSelectedIntegrationId] = React.useState('');
  const [selectedModel, setSelectedModel] = React.useState('');
  const [geminiModelIds, setGeminiModelIds] = React.useState<string[]>([]);
  const [geminiModelsLoading, setGeminiModelsLoading] = React.useState(false);
  const [geminiModelsError, setGeminiModelsError] = React.useState<string | null>(null);
  const [connectionModelAnchorEl, setConnectionModelAnchorEl] = React.useState<HTMLElement | null>(null);

  const [inputValue, setInputValue] = React.useState('');
  const [messages, setMessages] = React.useState<AskAIDialogMessageType[]>([]);
  const messagesRef = React.useRef<AskAIDialogMessageType[]>([]);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const supportDiagnostic = useSupportDiagnosticOptional();

  const selectedRecord = React.useMemo(
    () => localConnections.find((r) => r.id === selectedIntegrationId),
    [localConnections, selectedIntegrationId],
  );
  const apiKeyForDebounce = selectedRecord?.config.apiKey ?? '';
  const [debouncedGeminiKey] = useDebounce(apiKeyForDebounce, 600, { leading: false, trailing: true });

  const refreshLocalConnections = React.useCallback(() => {
    const rows = loadConnectedAiApiKeyIntegrationsFromBrowser();
    setLocalConnections(rows);
    if (!rows.length) {
      setSelectedIntegrationId('');
      setSelectedModel('');
      return;
    }
    const last = sessionStorage.getItem(LAST_INTEGRATION_SESSION_KEY);
    const pick = rows.find((r) => r.id === last) ?? rows[0];
    setSelectedIntegrationId(pick.id);
    setSelectedModel(pick.config.defaultModel?.trim() ?? '');
  }, []);

  React.useEffect(() => {
    refreshLocalConnections();
  }, [refreshLocalConnections]);

  React.useEffect(() => {
    let cancelled = false;
    void getCompanyAiStatus().then((res) => {
      if (cancelled) return;
      if (res.ok && res.status.status === 'ready' && res.status.provider) {
        setCompanyEngine({ loading: false, ready: true, provider: res.status.provider, model: res.status.model });
      } else {
        setCompanyEngine({ loading: false, ready: false, provider: null, model: null });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  //* מנוע החברה הוא ברירת המחדל — אלא אם המשתמש בחר במפורש חיבור מקומי בסשן הזה
  React.useEffect(() => {
    if (!companyEngine.ready) return;
    const last = sessionStorage.getItem(LAST_INTEGRATION_SESSION_KEY);
    const lastIsLocal = !!last && localConnections.some((r) => r.id === last);
    if (!lastIsLocal) setSelectedIntegrationId(COMPANY_ENGINE_ID);
  }, [companyEngine.ready, localConnections]);

  const usingCompanyEngine = selectedIntegrationId === COMPANY_ENGINE_ID && companyEngine.ready;
  const hasAnyConnection = localConnections.length > 0 || companyEngine.ready;

  React.useEffect(() => {
    if (usingCompanyEngine) sessionStorage.setItem(LAST_INTEGRATION_SESSION_KEY, COMPANY_ENGINE_ID);
  }, [usingCompanyEngine]);

  React.useEffect(() => {
    if (!persistLocalAiHistory) {
      setMessages([]);
      messagesRef.current = [];
      return;
    }
    const s = loadAiSessionFromBrowser(GLOBAL_APP_HELP_SESSION_ID);
    const next = s?.messages ?? [];
    setMessages(next);
    messagesRef.current = next;
  }, [persistLocalAiHistory]);

  React.useEffect(() => {
    if (!persistLocalAiHistory) return;
    saveAiSessionToBrowser(GLOBAL_APP_HELP_SESSION_ID, messages);
  }, [messages, persistLocalAiHistory]);

  React.useEffect(() => {
    if (!selectedRecord) return;
    sessionStorage.setItem(LAST_INTEGRATION_SESSION_KEY, selectedRecord.id);
    const defOpts = definitionModelOptions(selectedRecord.id);
    if (selectedRecord.id !== 'google_gemini_integration') {
      const m = selectedRecord.config.defaultModel?.trim();
      setSelectedModel(m && defOpts.includes(m) ? m : defOpts[0] ?? '');
      setGeminiModelIds([]);
      setGeminiModelsError(null);
      return;
    }
    const key = debouncedGeminiKey.trim();
    if (!key) {
      setGeminiModelIds([]);
      setGeminiModelsLoading(false);
      setGeminiModelsError(null);
      return;
    }
    let cancelled = false;
    setGeminiModelsLoading(true);
    setGeminiModelsError(null);
    void fetchGeminiModelsForGenerateContent(key).then((res) => {
      if (cancelled) return;
      setGeminiModelsLoading(false);
      if (res.ok) {
        setGeminiModelIds(res.modelIds);
        setGeminiModelsError(null);
        setSelectedModel((prev) => {
          const cur = prev.trim();
          if (cur && (res.modelIds.includes(cur) || cur === GEMINI_FALLBACK_MODEL)) return prev;
          if (selectedRecord.config.defaultModel?.trim() && res.modelIds.includes(selectedRecord.config.defaultModel.trim())) {
            return selectedRecord.config.defaultModel.trim();
          }
          return res.modelIds[0] ?? GEMINI_FALLBACK_MODEL;
        });
      } else {
        setGeminiModelIds([]);
        setGeminiModelsError(res.error);
        setSelectedModel((prev) => prev.trim() || GEMINI_FALLBACK_MODEL);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedRecord, debouncedGeminiKey]);

  const geminiModelSelectOptions = React.useMemo(() => {
    if (selectedIntegrationId !== 'google_gemini_integration') return [];
    let opts = [...geminiModelIds];
    if (opts.length === 0 && geminiModelsError) {
      opts = [GEMINI_FALLBACK_MODEL];
    }
    const cur = selectedModel.trim();
    if (cur && !opts.includes(cur)) opts = [cur, ...opts];
    return opts.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [selectedIntegrationId, geminiModelIds, geminiModelsError, selectedModel]);

  React.useEffect(() => {
    if (selectedIntegrationId !== 'google_gemini_integration') return;
    if (geminiModelsLoading) return;
    if (!geminiModelSelectOptions.length) return;
    if (selectedModel.trim() && geminiModelSelectOptions.includes(selectedModel)) return;
    setSelectedModel(geminiModelSelectOptions[0] ?? '');
  }, [selectedIntegrationId, geminiModelsLoading, geminiModelSelectOptions, selectedModel]);

  const handleMessages = (newMessages: AskAIDialogMessageType[]) => {
    setMessages(newMessages);
    messagesRef.current = newMessages;
  };

  const handleAddMessageToList = (message: AskAIDialogMessageType) => {
    const newMessages: AskAIDialogMessageType[] = JSON.parse(JSON.stringify(messagesRef.current));
    const i = newMessages.findIndex((msg) => msg.id === message.id);
    if (i !== -1) {
      newMessages[i] = message;
    } else {
      newMessages.push(message);
    }
    handleMessages(newMessages);
  };

  const resolvedLocalModel = React.useMemo(() => {
    if (!selectedRecord) return '';
    if (selectedRecord.id === 'google_gemini_integration') {
      return (
        selectedModel.trim() ||
        geminiModelSelectOptions[0] ||
        selectedRecord.config.defaultModel?.trim() ||
        GEMINI_FALLBACK_MODEL
      );
    }
    return selectedModel.trim() || definitionModelOptions(selectedRecord.id)[0] || '';
  }, [selectedRecord, selectedModel, geminiModelSelectOptions]);

  const canSendLocal =
    !!selectedRecord &&
    !!resolvedLocalModel &&
    !(selectedRecord.id === 'google_gemini_integration' && geminiModelsLoading && !geminiModelSelectOptions.length);
  const canSend = usingCompanyEngine || canSendLocal;

  const clearChat = React.useCallback(() => {
    clearAiSessionInBrowser(GLOBAL_APP_HELP_SESSION_ID);
    setMessages([]);
    messagesRef.current = [];
    enqueueSnackbar(h?.clearChat ?? 'Cleared', { variant: 'info' });
  }, [enqueueSnackbar, h?.clearChat]);

  const popoverSelectFormSx = { width: '100%', minWidth: 0 } as const;

  const askLoc = currentLocale?.chat?.chatHeader?.chatWindow?.askAI;

  const sendMessageToAI = async (directValue: string | null = null) => {
    const raw = directValue ?? inputValue;
    const currentValue = raw.trim();
    if (!currentValue) return;
    if (!directValue) setInputValue('');

    const locale = currentLanguage === 'he' ? 'he' : 'en';
    const agentDisplayName =
      [meUser?.FirstName, meUser?.LastName].filter(Boolean).join(' ').trim() ||
      meUser?.FullName?.trim() ||
      (locale === 'he' ? 'משתמש' : 'User');

    const systemPrompt = buildAppTechnicalSupportSystemPrompt({
      locale,
      currentPath: location.pathname,
      agentDisplayName,
      agentRole: meUser?.Role ?? null,
    });

    const newMessageObject: AskAIDialogMessageType = {
      id: generateUniqueId(
        messagesRef.current.map((msg) => msg.id),
        5,
      ),
      sender: 'user',
      message: currentValue,
    };
    handleAddMessageToList(newMessageObject);

    const loadingId = generateUniqueId(
      [...messagesRef.current.map((msg) => msg.id)],
      5,
    );
    const newAILoadingResponse: AskAIDialogMessageType = {
      id: loadingId,
      sender: 'AI',
      message: 'loading',
    };
    setTimeout(() => {
      handleAddMessageToList(newAILoadingResponse);
    }, 100);

    if (!canSend) {
      enqueueSnackbar(askLoc?.noConnectionOrModel ?? 'Choose a connection and model.', { variant: 'warning' });
      newAILoadingResponse.message = askLoc?.noConnectionOrModel ?? 'Configure connection';
      handleAddMessageToList(newAILoadingResponse);
      return;
    }

    try {
      const turns = messagesToTurns(messagesRef.current);
      if (!turns.length) {
        return;
      }
      //* מנוע החברה: דרך הנוד, המפתח נשאר בשרת. חיבור מקומי: ישירות מהדפדפן.
      const result = usingCompanyEngine
        ? await askCompanyAiSupportChat({ systemPrompt, turns }).then((r) =>
            r.ok ? ({ ok: true, text: r.text } as const) : ({ ok: false, error: r.message } as const),
          )
        : await askLocalLlmConversationWithSystem({
            integrationId: selectedRecord!.id,
            apiKey: selectedRecord!.config.apiKey!,
            model: resolvedLocalModel,
            systemPrompt,
            turns,
          });
      if (result.ok) {
        newAILoadingResponse.message = result.text;
        handleAddMessageToList(newAILoadingResponse);
      } else {
        enqueueSnackbar(result.error, { variant: 'error' });
        newAILoadingResponse.message = result.error;
        handleAddMessageToList(newAILoadingResponse);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      enqueueSnackbar(msg, { variant: 'error' });
      newAILoadingResponse.message = msg;
      handleAddMessageToList(newAILoadingResponse);
    }
  };

  const aiPaneMessages: AIAssistantPaneMessage[] = React.useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        role: m.sender === 'user' ? 'user' : 'assistant',
        text: m.sender === 'AI' && m.message === 'loading' ? '…' : m.message,
      })),
    [messages],
  );

  const suggestions: [string, string, string] = [h?.suggestion1, h?.suggestion2, h?.suggestion3].every(Boolean)
    ? [h!.suggestion1, h!.suggestion2, h!.suggestion3]
    : [
        currentLanguage === 'he' ? 'איך מגדירים מקורות הגעה?' : 'How do I set up lead sources?',
        currentLanguage === 'he' ? 'איפה מנהלים תבניות?' : 'Where are message templates managed?',
        currentLanguage === 'he' ? 'ההודעות לא נשלחות' : "Messages are not sent — what should I check?",
      ];

  const floatingHeaderIconSx = technicalSupportFloatingHeaderIconButtonSx(theme);
  const collapseLabel = currentLanguage === 'he' ? 'סגור' : 'Close';
  //* הווידג'ט הצף יושב מעל snackbar — כל דיאלוג/פופאובר שנפתח ממנו חייב שכבה גבוהה יותר,
  //* אחרת הוא נפתח מאחורי הווידג'ט ולא נגיש
  const overlayZIndex = theme.zIndex.snackbar + 2;

  const appHelpHeaderStartShortcuts = (
    <Stack
      component="nav"
      aria-label={h?.shortcutsNavAria}
      direction="row"
      useFlexGap
      spacing={0.25}
      sx={{
        flexShrink: 0,
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: 0.25,
        minWidth: 0,
      }}
    >
      <Tooltip
        title={h?.shortcutOnboarding ?? (currentLanguage === 'he' ? 'אשף התקנה' : 'Onboarding')}
        enterDelay={400}
      >
        <span>
          <IconButton
            type="button"
            size="small"
            onClick={() => revealOnboardingChecklist()}
            aria-label={h?.shortcutOnboarding ?? 'Onboarding'}
            sx={floatingHeaderIconSx}
          >
            <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0, opacity: 0.85 }}>
              <IconClipboardList size={16} stroke={1.5} aria-hidden />
            </Box>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={h?.shortcutInfoCenter ?? (currentLanguage === 'he' ? 'מרכז מידע' : 'Knowledge base')}
        enterDelay={400}
      >
        <span>
          <IconButton
            type="button"
            size="small"
            onClick={() => navigate('/info-center')}
            aria-label={h?.shortcutInfoCenter ?? 'Knowledge base'}
            sx={floatingHeaderIconSx}
          >
            <MenuBookOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={h?.shortcutContact ?? (currentLanguage === 'he' ? 'פניה לתמיכה' : 'Contact support')}
        enterDelay={400}
      >
        <span>
          <IconButton
            type="button"
            size="small"
            onClick={() => setContactModalOpen(true)}
            aria-label={h?.shortcutContact ?? 'Contact support'}
            sx={floatingHeaderIconSx}
          >
            <SupportAgentOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          </IconButton>
        </span>
      </Tooltip>
      {supportDiagnostic ? (
        <Tooltip
          title={h?.shortcutDiagnostic ?? (currentLanguage === 'he' ? 'אבחון תקלות' : 'Diagnostics')}
          enterDelay={400}
        >
          <span>
            <IconButton
              type="button"
              size="small"
              onClick={() => supportDiagnostic.openDialog()}
              aria-label={h?.shortcutDiagnostic ?? 'Diagnostics'}
              sx={floatingHeaderIconSx}
            >
              <BugReportOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </Stack>
  );

  return (
    <>
      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...technicalSupportAppShellProps(theme, 'floating'),
        }}
      >
        {!hasAnyConnection && companyEngine.loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !hasAnyConnection ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box component="header" dir={theme.direction} sx={technicalSupportFloatingBrandHeaderSx(theme)}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={technicalSupportFloatingAvatarSx(theme)}>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 24 }} aria-hidden />
                </Box>
                <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    component="h2"
                    fontWeight={700}
                    title={h?.panelTitle ?? 'Technical support (AI)'}
                    sx={{
                      fontSize: '1rem',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'inherit',
                    }}
                  >
                    {h?.panelTitle ?? 'Technical support (AI)'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: alpha(theme.palette.primary.contrastText, 0.82),
                      fontSize: '0.8125rem',
                      lineHeight: 1.35,
                    }}
                  >
                    {h?.panelSubtitle ?? (currentLanguage === 'he' ? 'איך אפשר לעזור היום?' : 'How can we help you today?')}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.125}>
                {appHelpHeaderStartShortcuts}
                <Tooltip title={collapseLabel}>
                  <IconButton
                    size="small"
                    onClick={closeGlobalAppHelpFloating}
                    aria-label={collapseLabel}
                    sx={floatingHeaderIconSx}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Stack
              spacing={2}
              sx={{
                flex: 1,
                minHeight: 0,
                px: 2.5,
                py: 3,
                justifyContent: 'center',
                alignItems: 'stretch',
                ...technicalSupportFloatingMessageAreaSx(theme),
              }}
            >
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.65, textAlign: 'start' }}>
                  {h?.noConnections}
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => {
                  //* הפאנל הוא שכבה צפה מעל הדף — בלי סגירה מסך החיבורים נפתח מאחוריו
                  closeGlobalAppHelpFloating();
                  navigate('/system-settings/connections');
                }}
                sx={{ alignSelf: 'stretch', py: 1, borderRadius: 2, fontWeight: 600 }}
              >
                {h?.goToConnections}
              </Button>
            </Stack>
          </Box>
        ) : (
          <>
            <Popover
        open={Boolean(connectionModelAnchorEl)}
        anchorEl={connectionModelAnchorEl}
        onClose={() => setConnectionModelAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: overlayZIndex }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 380,
              width: 'min(calc(100vw - 24px), 380px)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
              boxShadow: theme.shadows[8],
            },
          },
        }}
        disableScrollLock
      >
        <Stack spacing={1.5} sx={{ minWidth: 260 }}>
          <FormControl size="small" sx={popoverSelectFormSx}>
            <InputLabel id="app-help-connection-label">{askLoc?.connectionLabel ?? 'Connection'}</InputLabel>
            <Select
              labelId="app-help-connection-label"
              label={askLoc?.connectionLabel ?? 'Connection'}
              value={selectedIntegrationId}
              onChange={(e) => {
                const id = String(e.target.value);
                setSelectedIntegrationId(id);
                const row = localConnections.find((r) => r.id === id);
                if (row) setSelectedModel(row.config.defaultModel?.trim() ?? '');
              }}
              MenuProps={{ sx: { zIndex: overlayZIndex + 1 } }}
            >
              {companyEngine.ready && companyEngine.provider ? (
                <MenuItem value={COMPANY_ENGINE_ID}>
                  {(h?.companyEngine ?? 'Company engine ({provider})').replace(
                    '{provider}',
                    COMPANY_ENGINE_LABELS[companyEngine.provider],
                  )}
                </MenuItem>
              ) : null}
              {localConnections.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.provider}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {usingCompanyEngine ? (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'start' }}>
              {(h?.companyEngineModel ?? 'Model: {model} — set in the connections screen').replace(
                '{model}',
                companyEngine.model ?? '—',
              )}
            </Typography>
          ) : selectedRecord?.id === 'google_gemini_integration' ? (
            <FormControl
              size="small"
              sx={popoverSelectFormSx}
              disabled={!geminiModelSelectOptions.length && geminiModelsLoading}
            >
              <InputLabel id="app-help-model-gemini">{askLoc?.modelLabel ?? 'Model'}</InputLabel>
              <Select
                labelId="app-help-model-gemini"
                label={askLoc?.modelLabel ?? 'Model'}
                value={geminiModelSelectOptions.includes(selectedModel) ? selectedModel : geminiModelSelectOptions[0] ?? ''}
                onChange={(e) => setSelectedModel(String(e.target.value))}
                MenuProps={{ sx: { zIndex: overlayZIndex + 1 } }}
              >
                {geminiModelSelectOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : selectedRecord ? (
            <FormControl size="small" sx={popoverSelectFormSx}>
              <InputLabel id="app-help-model">{askLoc?.modelLabel ?? 'Model'}</InputLabel>
              <Select
                labelId="app-help-model"
                label={askLoc?.modelLabel ?? 'Model'}
                value={
                  definitionModelOptions(selectedRecord.id).includes(selectedModel)
                    ? selectedModel
                    : definitionModelOptions(selectedRecord.id)[0] ?? ''
                }
                onChange={(e) => setSelectedModel(String(e.target.value))}
                MenuProps={{ sx: { zIndex: overlayZIndex + 1 } }}
              >
                {definitionModelOptions(selectedRecord.id).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <Stack direction="row" alignItems="center" sx={{ minHeight: 28, gap: 0.5 }}>
            {selectedRecord?.id === 'google_gemini_integration' && geminiModelsLoading && (
              <Tooltip title="Loading models…">
                <CircularProgress size={18} sx={{ mx: 0.25 }} />
              </Tooltip>
            )}
            {selectedRecord?.id === 'google_gemini_integration' && geminiModelsError && (
              <Tooltip title={geminiModelsError}>
                <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 20 }} />
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Popover>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
        }}
      >
      <AIAssistantPane
        open
        embedInline
        uiVariant="technicalSupport"
        onClose={closeGlobalAppHelpFloating}
        title={h?.panelTitle ?? 'Technical support (AI)'}
        collapseAriaLabel={collapseLabel}
        headerSubtitle={h?.panelSubtitle}
        welcomeMessage={h?.welcomeMessage}
        messageListRef={messagesContainerRef}
        heightMode="floating"
        headerIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 24 }} aria-hidden />}
        headerStartContent={appHelpHeaderStartShortcuts}
        headerActions={
          <>
            <Tooltip title={h?.clearChatTooltip ?? h?.clearChat ?? 'Clear'}>
              <IconButton
                size="small"
                onClick={clearChat}
                color="inherit"
                aria-label={h?.clearChat ?? 'Clear'}
                sx={floatingHeaderIconSx}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} aria-hidden />
              </IconButton>
            </Tooltip>
            <Tooltip title={h?.connectionSettingsTooltip ?? 'Connection & model'}>
              <IconButton
                size="small"
                onClick={(e) => setConnectionModelAnchorEl(e.currentTarget)}
                aria-label={h?.connectionSettingsTooltip ?? 'Connection & model'}
                aria-haspopup="dialog"
                aria-expanded={Boolean(connectionModelAnchorEl)}
                sx={floatingHeaderIconSx}
              >
                <HubOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
              </IconButton>
            </Tooltip>
          </>
        }
        messages={aiPaneMessages}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSend={() => {
          void sendMessageToAI();
        }}
        onSuggestionClick={(s) => {
          setInputValue(s);
          void sendMessageToAI(s);
        }}
        suggestions={suggestions}
        inputPlaceholder={h?.inputPlaceholder ?? '…'}
        sendAriaLabel={currentLocale?.chat?.inputArea?.sendBtn?.default ?? 'Send'}
        /** שליחה — רק כשאין חיבור/מודל; אין לבטל את שדה הקלט כשהוא רק (אחרת לא אפשר להקליד לפני בחירה מהצ'יפים) */
        inputDisabled={!canSend}
        disableBackdrop
      />
      </Box>
          </>
        )}
      </Box>
      {/* הווידג'ט הצף יושב מעל snackbar — הדיאלוג חייב z-index גבוה יותר, אחרת הוא נפתח מאחוריו */}
      <ContactSupportModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        zIndex={overlayZIndex}
      />
    </>
  );
};

export default GlobalAppTechnicalSupportAI;
